import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {
  ORGANISER_EVENT_EDIT,
  ORGANISER_EVENT_PREVIEW,
  ORGANISER_MEASUREMENTS_MANAGEMENT
} from 'src/app/app-routing.module';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {AbstractComponent} from "@components";
import {
  Country,
  EventDetailsComponentType,
  EventFilter,
  EventItem,
  EventStatus,
  Lookups,
  Organiser,
  TOAST_STATE
} from "@model";
import {Address, AuthenticationService, EventService, GeolocationService, ToastService} from "@services";
import {alphabetical, max_length_255, required} from "@utility";
import {environment} from "@environment";

@Component({
    selector: 'app-event-basic-data',
    templateUrl: './event-basic-data.component.html',
    styleUrls: ['./event-basic-data.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1600)
            ])
        ])
    ],
    standalone: false
})
export class EventBasicDataComponent extends AbstractComponent implements OnInit {
  public static readonly EUR_CURRENCY: number = 7;
  currentDate: Date = new Date();
  isAdministrator: boolean = false;

  @Input() currentEventItem: EventItem | undefined;
  @Input() eventDetailsComponentType: EventDetailsComponentType | undefined;
  @Input() currencies: Lookups[] = [];
  @Input() countries: Lookups[] = [];
  @Input() eventTypes: Lookups[] = [];
  @Input() languages: Lookups[] = [];
  @Input() statuses: Lookups[] = [];
  @Input() sports: Lookups[] = [];
  @Input() measurers: Organiser[] = [];
  @Input() organisers: Organiser[] = [];
  @Input() userRole: String | undefined;

  filter: EventFilter = new EventFilter();

  inputForm = new FormGroup({
    organiserId: new FormControl(),
    measurerId: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    status: new FormControl(),
    imageMedium: new FormControl(),
    imageIcon: new FormControl(),
    latitude: new FormControl(),
    longitude: new FormControl(),
    defaultLanguage: new FormControl(),
    currency: new FormControl(),
    country: new FormControl(),
    city: new FormControl(),
    sportOne: new FormControl(),
    sportTwo: new FormControl(),
    extraLanguages: new FormControl(),
    eventType: new FormControl(),
    locationExtraTag: new FormControl(),
    commonStartNumber: new FormControl(),
    start: new FormControl(),
    end: new FormControl(),
    visibleToOtherLeagues: new FormControl(),
  });
  public isGeneralDataCollapsed: boolean = false;
  public isLocationDataCollapsed: boolean = false;

  constructor(private eventService: EventService,
              private toastService: ToastService, private router: Router,
              public translocoService: TranslocoService, private geolocationService: GeolocationService,
              public authenticationService: AuthenticationService) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.subscribeToDefaultLangChange();
    this.subscribeToOrganiserChange();
    this.eventItemChanged();
    this.isAdministrator = this.authenticationService.isAdministrator()
        || this.authenticationService.isOrganisersAdministrator();
  }

  private initForm() {
    this.inputForm.controls.organiserId.addValidators([required]);
    this.inputForm.controls.defaultLanguage.addValidators([required]);
    this.inputForm.controls.name.addValidators([required, max_length_255]);
    this.inputForm.controls.nameEng.addValidators([max_length_255]);
    this.inputForm.controls.nameIta.addValidators([max_length_255]);
    this.inputForm.controls.nameSlo.addValidators([max_length_255]);
    this.inputForm.controls.nameSr.addValidators([max_length_255]);
    this.inputForm.controls.nameBs.addValidators([max_length_255]);

    this.inputForm.controls.city.addValidators([required, alphabetical]);
    this.inputForm.controls.country.addValidators([required]);
    this.inputForm.controls.longitude.addValidators([required]);
    this.inputForm.controls.latitude.addValidators([required]);
    this.inputForm.controls.sportOne.addValidators([required]);
    this.inputForm.controls.status.addValidators([required]);
    this.inputForm.controls.eventType.addValidators([required]);
    this.inputForm.controls.start.addValidators([required]);
    this.inputForm.controls.end.addValidators([required]);


    if (environment.eventImageRequired) {
      this.inputForm.controls.imageIcon.addValidators([required]);
      this.inputForm.controls.imageMedium.addValidators([required]);
    }
    if (this.eventDetailsComponentType === EventDetailsComponentType.COPY) {
      this.inputForm.controls.status.disable();
    }
    if (this.organisers.length === 1) {
      this.inputForm.controls.organiserId.setValue(this.organisers[0].id);
    }
  }

  private subscribeToDefaultLangChange() {
    this.subscriptions.push(this.inputForm.controls.defaultLanguage.valueChanges.subscribe((langId: number) => {
      if (langId) {
        if (Array.isArray(this.inputForm.controls.extraLanguages.value)) {
          let extraLanguages = this.inputForm.controls.extraLanguages.value ? [...this.inputForm.controls.extraLanguages.value] : [];
          let index: number = extraLanguages.indexOf(langId, 0);
          if (index > -1) {
            extraLanguages.splice(index, 1);
            this.inputForm.controls.extraLanguages.setValue(extraLanguages);
          }
        }
      }
    }));
  }


  private eventItemChanged(): void {
    if (this.currentEventItem) {
      this.inputForm.patchValue(this.currentEventItem);
      this.eventService.getFilteredEventStatuses(this.currentEventItem.id ?? undefined).subscribe((statuses: Lookups[]): void => {
        this.statuses = statuses
      })

      if (!this.currentEventItem.currency) {
        this.inputForm.controls.currency.setValue(this.currentEventItem.organiser?.organiserPaymentData?.defaultCurrency ?? EventBasicDataComponent.EUR_CURRENCY);
      }
      if (!this.currentEventItem.status) {
        this.inputForm.controls.status.setValue(EventStatus.INACTIVE);
      }

      if (this.currentEventItem?.id && !(this.eventDetailsComponentType === EventDetailsComponentType.COPY)) {
        this.inputForm.controls.organiserId.disable();
      }
      this.inputForm.updateValueAndValidity();
    } else {
      this.inputForm.controls.commonStartNumber.setValue(true);
    }
  }

  imageChange(image: string | undefined) {
    this.inputForm.controls.imageMedium.setValue(image);
  }

  logoChange(image: string | undefined) {
    this.inputForm.controls.imageIcon.setValue(image);
  }

  private subscribeToOrganiserChange() {
    this.subscriptions.push(
      this.inputForm.controls.organiserId.valueChanges.subscribe((value: number): void => {
        let selectedOrganiser: Organiser = this.organisers.filter((organiser: Organiser): boolean => organiser.id === value)[0];
        if (selectedOrganiser?.organiserPaymentData) {
          this.inputForm.controls.currency.setValue(selectedOrganiser.organiserPaymentData?.defaultCurrency ?? EventBasicDataComponent.EUR_CURRENCY);
        } else {
          this.inputForm.controls.currency.setValue(EventBasicDataComponent.EUR_CURRENCY);
        }
      })
    )
  }

  goToEventPreview(): void {
    if (this.currentEventItem?.id) {
      this.eventService.setCurrentEvent(this.currentEventItem);
      this.router.navigate([ORGANISER_EVENT_PREVIEW], {queryParams: {eventId: this.currentEventItem.id}});
    }
  }

  goToEventMeasurements(): void {
      if (this.currentEventItem?.id) {
        this.eventService.setCurrentEvent(this.currentEventItem);
        this.router.navigate([ORGANISER_MEASUREMENTS_MANAGEMENT], {queryParams: {eventId: this.currentEventItem.id}});
      }
    }

  onSave(): void {
    this.markAllAsTouched(this.inputForm)
    if (!this.inputForm.valid) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.error.form"))
      return;
    } else {
      let eventDetails: EventItem = this.getEventFields();
      if (this.currentEventItem !== undefined) {
        if (this.eventDetailsComponentType === EventDetailsComponentType.COPY) {
          this.eventService.copyEvent(eventDetails, () => {
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("app.common.success.event.basic.data.edit"))
          }, () => {
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          }).subscribe((eventItem: EventItem | null) => {
            if (eventItem) {
              this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
            }
          });
        } else {
          this.eventService.updateEvent(eventDetails, () => {
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("app.common.success.event.basic.data.edit"))
          }, () => {
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          }).subscribe((eventItem: EventItem | null) => {
            this.currentEventItem = eventItem ?? undefined;
            this.eventItemChanged();
            if (eventItem) {
              if (eventDetails.id) {
                this.eventService.setCurrentEvent(eventDetails);
              } else {
                this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
              }
            }
          });
        }
      }
    }
  }

  getEventFields(): EventItem {
    this.checkValidators(this.inputForm);
    let eventDetails: EventItem = {...this.currentEventItem}
    eventDetails.organiserId = this.inputForm.value.organiserId;
    eventDetails.measurerId = this.inputForm.value.measurerId;
    eventDetails.organiser = this.organisers?.filter((organiser: Organiser) => organiser.id === this.inputForm.value.organiserId)[0];
    eventDetails.name = this.inputForm.value.name;
    eventDetails.nameEng = this.inputForm.value.nameEng;
    eventDetails.nameIta = this.inputForm.value.nameIta;
    eventDetails.nameSlo = this.inputForm.value.nameSlo;
    eventDetails.nameSr = this.inputForm.value.nameSr;
    eventDetails.nameBs = this.inputForm.value.nameBs;
    eventDetails.status = this.inputForm.value.status;
    eventDetails.start = this.inputForm.value.start;
    eventDetails.end = this.inputForm.value.end;
    eventDetails.latitude = this.inputForm.value.latitude;
    eventDetails.longitude = this.inputForm.value.longitude;
    eventDetails.defaultLanguage = this.inputForm.value.defaultLanguage;
    eventDetails.currency = this.inputForm.value.currency;
    eventDetails.country = this.inputForm.value.country;
    eventDetails.city = this.inputForm.value.city;
    eventDetails.sportOne = this.inputForm.value.sportOne;
    eventDetails.sportTwo = this.inputForm.value.sportTwo;
    eventDetails.extraLanguages = this.inputForm.controls.extraLanguages.value;
    eventDetails.eventType = this.inputForm.value.eventType;
    eventDetails.locationExtraTag = this.inputForm.value.locationExtraTag;
    eventDetails.imageIcon = this.inputForm.value.imageIcon;
    eventDetails.imageMedium = this.inputForm.value.imageMedium;
    eventDetails.commonStartNumber = this.inputForm.value.commonStartNumber;
    eventDetails.visibleToOtherLeagues = this.inputForm.value.visibleToOtherLeagues;
    return eventDetails;
  }

  getOptions(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    this.languages.map((language: Lookups): void => {
      if (language.id && language.id !== this.inputForm.controls.defaultLanguage.value) {
        optionsMap.set(language.id, (language?.code) ? this.translocoService.translate('app.common.language.' + language.code) : "")
      }
    });
    return optionsMap;
  }

  onSelectLocation(loc: any): void {
    this.inputForm.controls.latitude.setValue(loc.lat);
    this.inputForm.controls.longitude.setValue(loc.lng);
    if (loc.lat && loc.lng) {
      this.geolocationService.getLocationInfo(loc.lat, loc.lng).subscribe((address: Address | undefined) => {
        const country = this.countries.find((c: Country) => c.isoCode?.toString() === address?.country_code?.toUpperCase());
        this.inputForm.controls.country.setValue(country?.id);
        this.inputForm.controls.city.setValue(address?.city);
      });
    }

  }

  onEraseLocation() {
    this.inputForm.controls.latitude.reset();
    this.inputForm.controls.longitude.reset();
    this.inputForm.controls.country.reset();
    this.inputForm.controls.city.reset();
  }
}


